import { useSuspenseQuery } from '@apollo/client';
import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import { Headline } from '@xing-com/typography';

import { FormattedLink } from '../base-components';
import { FOOTER_EXTERNAL_LINKS, getFooterExternalLinks } from '../constants';
import { CareerHubTopicTitlesDocument } from '../graphql/career-hub-topic-titles-query.gql-types';

import { ParamsTrackingMapper } from './constants';
import { RelevantLinksData } from './relevant-links-data';
import { Styled } from './relevant-links.styles';
import { SkeletonRelevantLinks } from './skeleton-relevant-links';

type RelevantLinksProps = {
  topicKey?: string;
};

const RelevantLinksInner: React.FC<RelevantLinksProps> = ({ topicKey }) => {
  const { data } = useSuspenseQuery(CareerHubTopicTitlesDocument);

  const location = useLocation();
  const currentUrl = location.pathname;
  const trackingKey = topicKey && ParamsTrackingMapper.get(topicKey);
  const collection =
    data?.careerHubTopics?.collection ??
    (trackingKey && currentUrl.includes(trackingKey) ? RelevantLinksData : []);

  const topicsLinkKey = `?sc_o=career_hub_application_other_topics_click&sc_o_PropActionOrigin=career_hub_application_${topicKey}`;

  if (!(collection.length > 0)) return null;

  return (
    <Styled.GridItem span={12} spanMedium={6}>
      <Headline size="large" data-qa="logged-out-footer-topics" as="strong">
        <FormattedMessage id="CAREER_HUB_LOGOUT_FOOTER_TOPICS" />
      </Headline>
      {collection.map((articles, index) => (
        <FormattedLink
          href={`/karriere-ratgeber/${articles?.key}`}
          urlWithTracking={`/karriere-ratgeber/${articles?.key}${
            trackingKey ? topicsLinkKey : ''
          }`}
          key={articles?.key}
          text={articles?.title || ''}
          index={index}
        />
      ))}
    </Styled.GridItem>
  );
};

export const RelevantLinks: React.FC<RelevantLinksProps> = ({ topicKey }) => {
  const servicesLinkBox = topicKey
    ? getFooterExternalLinks(topicKey)
    : FOOTER_EXTERNAL_LINKS;

  return (
    <Styled.GridContainer variant="FullSize">
      <Styled.GridItem span={12} spanMedium={6}>
        <Headline size="large" as="strong">
          <FormattedMessage id="CAREER_HUB_LOGOUT_FOOTER_SERVICES" />
        </Headline>
        {servicesLinkBox.map(({ href, text }, index) => (
          <FormattedLink {...{ href, text, index }} key={text} isExternal />
        ))}
      </Styled.GridItem>
      <Suspense fallback={<SkeletonRelevantLinks />}>
        <RelevantLinksInner topicKey={topicKey} />
      </Suspense>
    </Styled.GridContainer>
  );
};
